import React from "react";
import {
  Box,
  Typography,
  Divider,
  Container,
  Chip,
  Stack,
} from "@mui/material";
import { Shield as ShieldIcon } from "@mui/icons-material";

const LAST_UPDATED = "May 5, 2025";
const COMPANY_NAME = "PresentifyClone Inc.";
const CONTACT_EMAIL = "lag.webservices@gmail.com";
const APP_NAME = "PresentifyClone";

const Section = ({ title, children }) => (
  <Box sx={{ mb: 4 }}>
    <Typography variant="h6" fontWeight={600} gutterBottom color="text.primary">
      {title}
    </Typography>
    {children}
  </Box>
);

const BodyText = ({ children, sx = {} }) => (
  <Typography variant="body2" color="text.secondary" lineHeight={1.8} sx={{ mb: 1.5, ...sx }}>
    {children}
  </Typography>
);

const BulletList = ({ items }) => (
  <Box component="ul" sx={{ pl: 2.5, mt: 0.5, mb: 1.5 }}>
    {items.map((item, i) => (
      <Box component="li" key={i} sx={{ mb: 0.75 }}>
        <Typography variant="body2" color="text.secondary" lineHeight={1.8}>
          {item}
        </Typography>
      </Box>
    ))}
  </Box>
);

const PrivacyPolicy = () => {
  return (
    <Container maxWidth="md" sx={{ py: { xs: 4, md: 8 } }}>

      {/* Header */}
      <Box sx={{ mb: 5 }}>
        <Stack direction="row" alignItems="center" spacing={1.5} mb={1.5}>
          <ShieldIcon color="primary" />
          <Typography variant="h4" fontWeight={700} color="text.primary">
            Privacy Policy
          </Typography>
        </Stack>
        <Stack direction="row" spacing={1} alignItems="center">
          <Chip label={`Last updated: ${LAST_UPDATED}`} size="small" variant="outlined" />
          <Chip label={COMPANY_NAME} size="small" color="primary" variant="outlined" />
        </Stack>
        <Typography variant="body2" color="text.secondary" lineHeight={1.8} sx={{ mt: 2 }}>
          This Privacy Policy describes how {COMPANY_NAME} ("{APP_NAME}", "we", "us", or "our")
          collects, uses, and shares information about you when you use our services, including
          our platform, website, and related applications (collectively, the "Services").
          By using our Services, you agree to the collection and use of information in
          accordance with this policy.
        </Typography>
      </Box>

      <Divider sx={{ mb: 4 }} />

      {/* 1 */}
      <Section title="1. Information We Collect">
        <BodyText>
          We collect information you provide directly to us, information we collect
          automatically when you use our Services, and information from third-party sources.
        </BodyText>

        <Typography variant="subtitle2" fontWeight={600} color="text.primary" gutterBottom>
          Information you provide directly
        </Typography>
        <BulletList items={[
          "Account registration details such as your name, email address, and password.",
          "Profile information including job title, organization, and profile photo.",
          "Payment and billing information processed through our third-party payment providers.",
          "Communications you send us, including support requests and feedback.",
          "Any content, data, or files you upload or create within the Services.",
        ]} />

        <Typography variant="subtitle2" fontWeight={600} color="text.primary" gutterBottom>
          Information collected automatically
        </Typography>
        <BulletList items={[
          "Log data: IP address, browser type, operating system, referring URLs, and pages visited.",
          "Device information: hardware model, operating system version, and unique device identifiers.",
          "Usage data: features accessed, actions taken, time spent, and interaction patterns.",
          "Cookies and similar tracking technologies as described in our Cookie Policy.",
          "Location data derived from your IP address or, with your consent, precise GPS data.",
        ]} />

        <Typography variant="subtitle2" fontWeight={600} color="text.primary" gutterBottom>
          Information from third parties
        </Typography>
        <BulletList items={[
          "Single sign-on providers (e.g., Google, Microsoft) if you choose to authenticate via OAuth.",
          "Analytics and advertising partners who help us understand how our Services are used.",
          "Publicly available sources to verify identity or supplement your profile information.",
        ]} />
      </Section>

      {/* 2 */}
      <Section title="2. How We Use Your Information">
        <BodyText>
          We use the information we collect for the following purposes:
        </BodyText>
        <BulletList items={[
          "To provide, maintain, and improve our Services and develop new features.",
          "To process transactions and send related information including confirmations and invoices.",
          "To send administrative information such as changes to our terms, conditions, and policies.",
          "To respond to your comments, questions, and customer service requests.",
          "To send promotional communications, where permitted by law and your preferences.",
          "To monitor and analyze usage trends and activities in connection with our Services.",
          "To detect, investigate, and prevent fraudulent transactions and other illegal activities.",
          "To personalize your experience and deliver content relevant to your interests.",
          "To comply with legal obligations and enforce our agreements.",
          "To protect the rights, property, and safety of our users and the public.",
        ]} />
      </Section>

      {/* 3 */}
      <Section title="3. Sharing of Information">
        <BodyText>
          We do not sell your personal information. We may share your information in
          the following circumstances:
        </BodyText>
        <BulletList items={[
          "With service providers who perform services on our behalf such as hosting, analytics, payment processing, and customer support.",
          "With your organization or account administrator if your account is managed through an enterprise plan.",
          "With other users within your organization's workspace, consistent with your sharing and permission settings.",
          "In connection with a merger, acquisition, or sale of all or a portion of our assets.",
          "In response to a valid legal request, court order, or governmental regulation.",
          "When we believe disclosure is necessary to protect the rights, property, or safety of our users or others.",
          "With your explicit consent for any other purpose not listed here.",
        ]} />
      </Section>

      {/* 4 */}
      <Section title="4. Data Retention">
        <BodyText>
          We retain your personal information for as long as necessary to fulfill the purposes
          outlined in this Privacy Policy, unless a longer retention period is required or
          permitted by law.
        </BodyText>
        <BulletList items={[
          "Active account data is retained for the lifetime of your account.",
          "After account deletion, we may retain anonymized or aggregated data indefinitely.",
          "Billing and transaction records are retained for up to 7 years for legal and tax compliance.",
          "Backup data may persist for up to 90 days following deletion requests before being purged.",
        ]} />
      </Section>

      {/* 5 */}
      <Section title="5. Data Security">
        <BodyText>
          We take reasonable and appropriate technical and organizational measures to protect
          your personal information from unauthorized access, alteration, disclosure, or
          destruction. These measures include:
        </BodyText>
        <BulletList items={[
          "Encryption of data in transit using TLS 1.2 or higher.",
          "Encryption of data at rest using AES-256.",
          "Role-based access controls limiting internal access to personal data.",
          "Regular security assessments, penetration testing, and vulnerability scanning.",
          "Employee training on data privacy and security practices.",
        ]} />
        <BodyText>
          While we strive to use commercially acceptable means to protect your information,
          no method of transmission over the Internet or method of electronic storage is 100%
          secure. We cannot guarantee absolute security.
        </BodyText>
      </Section>

      {/* 6 */}
      <Section title="6. Your Rights and Choices">
        <BodyText>
          Depending on your jurisdiction, you may have certain rights regarding your
          personal information. These may include:
        </BodyText>
        <BulletList items={[
          "Access: the right to request a copy of the personal information we hold about you.",
          "Correction: the right to request that we correct inaccurate or incomplete information.",
          "Deletion: the right to request that we delete your personal information, subject to legal obligations.",
          "Portability: the right to receive your data in a structured, machine-readable format.",
          "Objection: the right to object to our processing of your data for direct marketing purposes.",
          "Restriction: the right to request that we restrict processing of your information under certain circumstances.",
          "Withdrawal of consent: the right to withdraw consent where processing is based on consent.",
        ]} />
        <BodyText>
          To exercise any of these rights, please contact us at{" "}
          <Box component="span" sx={{ color: "primary.main", fontWeight: 600 }}>
            {CONTACT_EMAIL}
          </Box>
          . We will respond to your request within 30 days.
        </BodyText>
      </Section>

      {/* 7 */}
      <Section title="7. Cookies and Tracking Technologies">
        <BodyText>
          We use cookies and similar tracking technologies to collect and track information
          about your use of our Services. Cookies are small data files stored on your device.
          We use the following types of cookies:
        </BodyText>
        <BulletList items={[
          "Essential cookies: necessary for the Services to function and cannot be disabled.",
          "Performance cookies: help us understand how visitors interact with our Services.",
          "Functional cookies: remember your preferences and personalize your experience.",
          "Analytics cookies: collect anonymized data to help us improve our Services.",
        ]} />
        <BodyText>
          You can control cookie settings through your browser settings. Disabling certain
          cookies may limit the functionality of our Services.
        </BodyText>
      </Section>

      {/* 8 */}
      <Section title="8. International Data Transfers">
        <BodyText>
          {COMPANY_NAME} is headquartered in the United States. If you access our Services
          from outside the United States, your information may be transferred to, stored, and
          processed in the United States or other countries where our servers are located. We
          ensure appropriate safeguards are in place for such transfers, including Standard
          Contractual Clauses approved by the European Commission where applicable.
        </BodyText>
      </Section>

      {/* 9 */}
      <Section title="9. Children's Privacy">
        <BodyText>
          Our Services are not directed to individuals under the age of 16. We do not
          knowingly collect personal information from children under 16. If we become aware
          that we have collected personal information from a child under 16, we will take
          steps to delete that information promptly. If you believe we have inadvertently
          collected information from a child, please contact us at {CONTACT_EMAIL}.
        </BodyText>
      </Section>

      {/* 10 */}
      <Section title="10. Changes to This Policy">
        <BodyText>
          We may update this Privacy Policy from time to time. We will notify you of
          any material changes by posting the new policy on this page and updating the
          "Last updated" date. For significant changes, we may also send an email
          notification. We encourage you to review this policy periodically.
          Your continued use of the Services after any changes constitutes your acceptance
          of the revised policy.
        </BodyText>
      </Section>

      {/* 11 */}
      <Section title="11. Contact Us">
        <BodyText>
          If you have any questions, concerns, or requests regarding this Privacy Policy
          or our data practices, please contact us:
        </BodyText>
        <Box
          sx={{
            p: 2.5,
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 2,
            bgcolor: "action.hover",
          }}
        >
          <Typography variant="body2" color="text.primary" fontWeight={600}>
            {COMPANY_NAME}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Privacy & Data Protection Team
          </Typography>
          <Typography variant="body2" color="primary.main" sx={{ mt: 0.5 }}>
            {CONTACT_EMAIL}
          </Typography>
        </Box>
      </Section>

      <Divider sx={{ mt: 2, mb: 3 }} />
      <Typography variant="caption" color="text.disabled">
        © {new Date().getFullYear()} {COMPANY_NAME}. All rights reserved.
      </Typography>
    </Container>
  );
};

export default PrivacyPolicy;